import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useParams, Link, useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import universeService from '../../services/universeService';
import forumService from '../../services/forumService';
import threadService from '../../services/threadService';
import draftService, { hasSubstantialHtml } from '../../services/draftService';
import { characterApi } from '../../services/characterApi';
import factionService from '../../services/factionService';
import rpActivityService from '../../services/rpActivityService';
import dialogueThemeService from '../../services/dialogueThemeService';
import DialogueThemePanel from '../../components/DialogueThemePanel/DialogueThemePanel';
import RichTextComposer from '../../components/RichTextComposer/RichTextComposerTiptap';
import { useUniverseTheme } from '../../contexts/UniverseThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import useThreadFilters from '../../hooks/useThreadFilters';
import Layout from '../../components/Layout/Layout';
import SeoHead from '../../components/Seo/SeoHead';
import Breadcrumb from '../../components/Breadcrumb/Breadcrumb';
import Loading from '../../components/Loading/Loading';
import ErrorMessage from '../../components/ErrorMessage/ErrorMessage';
import ForumCardV3 from '../../components/ForumCardV3/ForumCardV3';
import ElseworldCard from '../../components/ElseworldCard/ElseworldCard';
import FilterToggle from '../../components/FilterToggle/FilterToggle';
import Pagination from '../../components/Pagination/Pagination';
import ThreadFilters from '../../components/ThreadFilters/ThreadFilters';
import ThreadStatusBadges from '../../components/ThreadStatusBadges/ThreadStatusBadges';
import ForumThreadModerationActions from '../../components/ForumThreadModerationActions/ForumThreadModerationActions';
import RpActivityCard from '../../components/RpActivityCard/RpActivityCard';
import { normalizeForum } from '../../utils/forumUtils';
import { canModerateStaff } from '../../utils/staffPermissions';
import { collectForumMoveOptions } from '../../utils/forumMoveOptions';
import seoService from '../../services/seoService';
import { getUniverseForumsPath, getElseworldForumsPath } from '../../utils/universePaths';
import { getCharacterProfilePath } from '../../utils/characterPaths';
import styles from './ForumDetail.module.css';

const FILTER_ALL = 'all';
const FILTER_IMPORTANT = 'important';
const FILTER_PLAYER_PLATFORM = 'player_platform';
const FILTER_RP = 'roleplay';
const FILTER_HRP = 'hrp';
const FORUM_LAYOUT_SINGLE = 'single';
const FORUM_LAYOUT_DOUBLE = 'double';
const FORUM_CARD_VARIANT_DEFAULT = 'default';
const FORUM_CARD_VARIANT_COMPACT = 'compact';
const ACTIVITY_FILTER_ALL = 'all';
const ACTIVITY_FILTER_EVENT = 'event';
const ACTIVITY_FILTER_MISSION = 'mission';
/** Une carte d’activité RP par slide (carrousel). */
const ACTIVITY_CARDS_PER_SLIDE = 1;

const FORUM_TYPE_OPTIONS = [
  { value: FILTER_ALL, label: 'Tous' },
  { value: FILTER_IMPORTANT, label: 'Importants' },
  { value: FILTER_RP, label: 'RP' },
  { value: FILTER_HRP, label: 'HRP' },
];

const FORUM_LAYOUT_OPTIONS = [
  { value: FORUM_LAYOUT_SINGLE, label: '1 col.' },
  { value: FORUM_LAYOUT_DOUBLE, label: '2 col.' },
];

const FORUM_VARIANT_OPTIONS = [
  { value: FORUM_CARD_VARIANT_DEFAULT, label: 'Bannière' },
  { value: FORUM_CARD_VARIANT_COMPACT, label: 'Compact' },
];

const MultiSelectField = ({
  label,
  options = [],
  selectedValues = [],
  onChange,
  placeholder = 'Sélectionner...',
  emptyText = 'Aucune option',
  getOptionLabel = (option) => option.label || option.name || String(option.id),
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const rootRef = useRef(null);

  useEffect(() => {
    const onDocumentClick = (event) => {
      if (!rootRef.current?.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocumentClick);
    return () => document.removeEventListener('mousedown', onDocumentClick);
  }, []);

  const selectedSet = useMemo(() => new Set(selectedValues.map((value) => String(value))), [selectedValues]);

  const selectedOptions = useMemo(
    () => options.filter((option) => selectedSet.has(String(option.id))),
    [options, selectedSet]
  );

  const filteredOptions = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return options;
    return options.filter((option) => getOptionLabel(option).toLowerCase().includes(query));
  }, [options, search, getOptionLabel]);

  const toggleValue = (value) => {
    const normalized = String(value);
    if (selectedSet.has(normalized)) {
      onChange(selectedValues.filter((item) => String(item) !== normalized));
    } else {
      onChange([...selectedValues, normalized]);
    }
  };

  return (
    <div className={styles.multiSelectField} ref={rootRef}>
      <label className={styles.createThreadLabel}>{label}</label>
      <button type="button" className={styles.multiSelectControl} onClick={() => setIsOpen((prev) => !prev)}>
        <span className={styles.multiSelectValueText}>
          {selectedOptions.length > 0
            ? `${selectedOptions.length} sélectionné(s)`
            : placeholder}
        </span>
        <span className={`${styles.multiSelectChevron} ${isOpen ? styles.multiSelectChevronOpen : ''}`}>▾</span>
      </button>
      {selectedOptions.length > 0 && (
        <div className={styles.multiSelectTags}>
          {selectedOptions.map((option) => (
            <span key={option.id} className={styles.multiSelectTag}>
              {getOptionLabel(option)}
            </span>
          ))}
        </div>
      )}
      {isOpen && (
        <div className={styles.multiSelectDropdown}>
          <input
            type="text"
            className={styles.multiSelectSearch}
            placeholder="Rechercher..."
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
          <div className={styles.multiSelectOptions}>
            {filteredOptions.length > 0 ? filteredOptions.map((option) => {
              const optionLabel = getOptionLabel(option);
              const isSelected = selectedSet.has(String(option.id));
              return (
                <label key={option.id} className={styles.multiSelectOption}>
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => toggleValue(option.id)}
                  />
                  <span>{optionLabel}</span>
                </label>
              );
            }) : (
              <span className={styles.createThreadHint}>{emptyText}</span>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const ForumDetail = () => {
  const { id, slug, elseworldSlug } = useParams();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { currentUniverse, currentElseworld, setUniverseTheme, setElseworldTheme, clearElseworldTheme } = useUniverseTheme();
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();

  const isElseworldRoute = Boolean(elseworldSlug);
  const isUniverseRoute = location.pathname.startsWith('/univers/') && !isElseworldRoute;
  const forumSlug = !isUniverseRoute && !isElseworldRoute && (slug || id) ? (slug || id) : null;
  const universeSlug = useMemo(() => {
    if ((isUniverseRoute || isElseworldRoute) && slug) {
      return slug;
    }
    return currentUniverse !== 'portal' ? currentUniverse : null;
  }, [isUniverseRoute, isElseworldRoute, slug, currentUniverse]);
  
  // États pour les données
  const [forumData, setForumData] = useState(null);
  const [singleForumData, setSingleForumData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeFilter, setActiveFilter] = useState(FILTER_ALL);
  const [searchQuery, setSearchQuery] = useState('');
  const [isSingleForum, setIsSingleForum] = useState(false);
  const [forumLayout, setForumLayout] = useState(FORUM_LAYOUT_SINGLE);
  const [forumCardVariant, setForumCardVariant] = useState(FORUM_CARD_VARIANT_DEFAULT);
  const [isCreateThreadOpen, setIsCreateThreadOpen] = useState(false);
  const [creatingThread, setCreatingThread] = useState(false);
  const [createThreadError, setCreateThreadError] = useState('');
  const [newThreadTitle, setNewThreadTitle] = useState('');
  const [newThreadContent, setNewThreadContent] = useState('');
  const [newThreadCharacterId, setNewThreadCharacterId] = useState('');
  const [ownedThreadCharacters, setOwnedThreadCharacters] = useState([]);
  const [universeThreadCharacters, setUniverseThreadCharacters] = useState([]);
  const [threadFactions, setThreadFactions] = useState([]);
  const [newThreadParticipantIds, setNewThreadParticipantIds] = useState([]);
  const [newThreadFactionIds, setNewThreadFactionIds] = useState([]);
  const [threadDraftId, setThreadDraftId] = useState(null);
  const [threadDraftSavedAt, setThreadDraftSavedAt] = useState(null);
  const [threadDraftMessage, setThreadDraftMessage] = useState('');
  const [savingThreadDraft, setSavingThreadDraft] = useState(false);
  const [dialogueThemes, setDialogueThemes] = useState([]);
  const [activeDialogueThemeId, setActiveDialogueThemeId] = useState('');
  const [rpActivities, setRpActivities] = useState([]);
  const [rpActivitiesLoading, setRpActivitiesLoading] = useState(false);
  const [pageSeo, setPageSeo] = useState(null);
  const [rpActivityFilter, setRpActivityFilter] = useState(ACTIVITY_FILTER_ALL);
  const [activitySlideIndex, setActivitySlideIndex] = useState(0);
  const [activitySelectableCharacters, setActivitySelectableCharacters] = useState([]);
  const [isModerationMode, setIsModerationMode] = useState(false);
  const [forumMoveOptions, setForumMoveOptions] = useState([]);
  const [loadingForumMoveOptions, setLoadingForumMoveOptions] = useState(false);
  const [universeElseworlds, setUniverseElseworlds] = useState([]);
  const [moderationError, setModerationError] = useState('');
  const isRoleplayForum = Boolean(singleForumData?.isRoleplay || singleForumData?.type === 'roleplay');
  const canModerateThreads = useMemo(() => canModerateStaff(user), [user]);
  
  // Hook personnalisé pour les filtres de threads (utilise l'URL comme source de vérité)
  const {
    filters: threadFilters,
    currentPage,
    searchInput,
    apiFilters,
    setStatus,
    setSearch,
    setMyParticipations,
    setPage,
    hasActiveFilters,
  } = useThreadFilters(user?.id);

  useEffect(() => {
    if (isElseworldRoute && slug && elseworldSlug) {
      setElseworldTheme(slug, elseworldSlug);
      return;
    }

    if (isUniverseRoute && slug && slug !== currentUniverse) {
      setUniverseTheme(slug);
    }
  }, [isElseworldRoute, isUniverseRoute, slug, elseworldSlug, currentUniverse, setUniverseTheme, setElseworldTheme]);

  useEffect(() => {
    if (!isSingleForum || !singleForumData) {
      return;
    }

    const forumUniverseSlug = singleForumData.universe?.slug;
    const forumElseworldSlug = singleForumData.elseworld?.slug;

    if (forumElseworldSlug && forumUniverseSlug) {
      setElseworldTheme(forumUniverseSlug, forumElseworldSlug);
      return;
    }

    if (forumUniverseSlug) {
      setUniverseTheme(forumUniverseSlug);
    }
  }, [isSingleForum, singleForumData, setElseworldTheme, setUniverseTheme]);

  // Charger les données du forum une seule fois (sans filtres)
  useEffect(() => {
    const fetchData = async () => {
      try {
        setError(null);
        setLoading(true);

        if (forumSlug) {
          setIsSingleForum(true);
          const data = await forumService.getForumBySlug(forumSlug, {
            page: currentPage,
            limit: 10,
            filters: apiFilters,
          });
          setSingleForumData(data);
        } else {
          setIsSingleForum(false);

          if (!universeSlug) {
            setError('Veuillez sélectionner un univers pour voir ses forums');
            setLoading(false);
            return;
          }

          if (isElseworldRoute && elseworldSlug) {
            const data = await universeService.getElseworldForums(universeSlug, elseworldSlug);
            setForumData(data);
            setUniverseElseworlds([]);
            setRpActivities([]);
            setRpActivitiesLoading(false);
          } else {
            const data = await universeService.getUniversForums(universeSlug);
            setForumData(data);
            try {
              const elseworlds = await universeService.getUniverseElseworlds(universeSlug);
              setUniverseElseworlds(Array.isArray(elseworlds) ? elseworlds : []);
            } catch (elseworldError) {
              console.error(elseworldError);
              setUniverseElseworlds([]);
            }
            setRpActivitiesLoading(true);
            try {
              const activityData = await rpActivityService.getUniverseActivities(universeSlug);
              setRpActivities(Array.isArray(activityData?.items) ? activityData.items : []);
            } catch (activityError) {
              console.error(activityError);
              setRpActivities([]);
            } finally {
              setRpActivitiesLoading(false);
            }
          }
        }
      } catch (err) {
        setError('Erreur lors du chargement des données');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [forumSlug, universeSlug, elseworldSlug, isElseworldRoute, currentPage, apiFilters]);

  useEffect(() => {
    const loadUniverseSeo = async () => {
      if (forumSlug) {
        setPageSeo(null);
        return;
      }

      if (!universeSlug) {
        setPageSeo(null);
        return;
      }

      try {
        const data = await seoService.getUniverse(universeSlug);
        setPageSeo(data?.seo || null);
      } catch (err) {
        setPageSeo(null);
      }
    };

    loadUniverseSeo();
  }, [forumSlug, universeSlug]);

  useEffect(() => {
    const fetchActivityCharacters = async () => {
      if (!isAuthenticated || !currentUniverse || currentUniverse === 'portal') {
        setActivitySelectableCharacters([]);
        return;
      }

      try {
        const data = await characterApi.getUniverseSelectableCharacters(currentUniverse);
        setActivitySelectableCharacters(Array.isArray(data?.items) ? data.items : []);
      } catch (err) {
        console.error(err);
        setActivitySelectableCharacters([]);
      }
    };

    fetchActivityCharacters();
  }, [isAuthenticated, currentUniverse]);

  useEffect(() => {
    setIsCreateThreadOpen(false);
    setCreatingThread(false);
    setCreateThreadError('');
    setNewThreadTitle('');
    setNewThreadContent('');
    setNewThreadCharacterId('');
    setOwnedThreadCharacters([]);
    setUniverseThreadCharacters([]);
    setThreadFactions([]);
    setNewThreadParticipantIds([]);
    setNewThreadFactionIds([]);
    setDialogueThemes([]);
    setActiveDialogueThemeId('');
  }, [singleForumData?.forumId]);

  useEffect(() => {
    const fetchDialogueThemes = async () => {
      if (!isSingleForum || !isRoleplayForum || !isCreateThreadOpen || !isAuthenticated || !user) {
        setDialogueThemes([]);
        setActiveDialogueThemeId('');
        return;
      }

      try {
        const data = await dialogueThemeService.listThemes();
        const items = Array.isArray(data?.items) ? data.items : [];
        setDialogueThemes(items);
        const defaultThemeId = data?.defaultThemeId
          || items.find((theme) => theme.isDefault)?.id
          || items[0]?.id
          || '';
        setActiveDialogueThemeId(defaultThemeId ? String(defaultThemeId) : '');
      } catch (err) {
        console.error(err);
        setDialogueThemes([]);
        setActiveDialogueThemeId('');
      }
    };

    fetchDialogueThemes();
  }, [isSingleForum, isRoleplayForum, isCreateThreadOpen, isAuthenticated, user]);

  useEffect(() => {
    const fetchCharacters = async () => {
      if (!isSingleForum || !isRoleplayForum || !isCreateThreadOpen || !isAuthenticated || !user) {
        return;
      }

      try {
        const universeSlug = singleForumData?.universe?.slug;
        if (!universeSlug) {
          setOwnedThreadCharacters([]);
          setUniverseThreadCharacters([]);
          return;
        }

        const ownData = await characterApi.getUniverseSelectableCharacters(universeSlug);
        const ownItems = Array.isArray(ownData?.items) ? ownData.items : [];
        setOwnedThreadCharacters(ownItems);
        if (ownItems.length > 0 && !newThreadCharacterId) {
          const firstId = String(ownItems[0].id);
          setNewThreadCharacterId(firstId);
          setNewThreadParticipantIds([firstId]);
        }

        const universeData = await characterApi.getUniverseValidatedCharacters(universeSlug);
        const universeItems = Array.isArray(universeData?.items) ? universeData.items : [];
        setUniverseThreadCharacters(universeItems);
      } catch (err) {
        console.error(err);
        setOwnedThreadCharacters([]);
        setUniverseThreadCharacters([]);
      }
    };

    fetchCharacters();
  }, [
    isSingleForum,
    isRoleplayForum,
    singleForumData?.universe?.slug,
    isCreateThreadOpen,
    isAuthenticated,
    user,
    newThreadCharacterId,
  ]);

  useEffect(() => {
    const fetchFactions = async () => {
      if (!isSingleForum || !isRoleplayForum || !isCreateThreadOpen || !isAuthenticated || !user) {
        return;
      }

      try {
        const universeSlug = singleForumData?.universe?.slug;
        if (!universeSlug) {
          setThreadFactions([]);
          return;
        }

        const data = await factionService.getFactions({ universe: universeSlug });
        const factions = Array.isArray(data?.factions) ? data.factions : [];
        setThreadFactions(factions);
      } catch (err) {
        console.error(err);
        setThreadFactions([]);
      }
    };

    fetchFactions();
  }, [isSingleForum, isRoleplayForum, singleForumData?.universe?.slug, isCreateThreadOpen, isAuthenticated, user]);

  useEffect(() => {
    if (!newThreadCharacterId) return;
    setNewThreadParticipantIds((prev) => (
      prev.includes(newThreadCharacterId) ? prev : [newThreadCharacterId, ...prev]
    ));
  }, [newThreadCharacterId]);

  useEffect(() => {
    if (!isModerationMode || !canModerateThreads) {
      setForumMoveOptions([]);
      return undefined;
    }

    const universeSlugForMove = singleForumData?.universe?.slug || universeSlug;
    const elseworldSlugForMove = singleForumData?.elseworld?.slug || (isElseworldRoute ? elseworldSlug : null);
    if (!universeSlugForMove) return undefined;

    let cancelled = false;
    setLoadingForumMoveOptions(true);
    const loadForums = elseworldSlugForMove
      ? universeService.getElseworldForums(universeSlugForMove, elseworldSlugForMove)
      : universeService.getUniversForums(universeSlugForMove);

    loadForums
      .then((data) => {
        if (!cancelled) {
          setForumMoveOptions(collectForumMoveOptions(data));
        }
      })
      .catch(() => {
        if (!cancelled) {
          setForumMoveOptions([]);
          setModerationError('Impossible de charger la liste des forums pour le déplacement.');
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoadingForumMoveOptions(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [isModerationMode, canModerateThreads, singleForumData?.universe?.slug, singleForumData?.elseworld?.slug, universeSlug, isElseworldRoute, elseworldSlug]);

  useEffect(() => {
    if (!canModerateThreads) {
      setIsModerationMode(false);
    }
  }, [canModerateThreads]);

  const handleThreadModerationUpdated = useCallback((threadId, updates) => {
    setSingleForumData((prev) => {
      if (!prev?.threads) return prev;
      return {
        ...prev,
        threads: prev.threads.map((thread) => (
          thread.threadId === threadId
            ? {
              ...thread,
              ...updates,
              locked: updates.locked ?? (updates.status === 'closed'),
            }
            : thread
        )),
      };
    });
    setModerationError('');
  }, []);

  const handleThreadModerationMoved = useCallback((threadId) => {
    setSingleForumData((prev) => {
      if (!prev?.threads) return prev;
      return {
        ...prev,
        threads: prev.threads.filter((thread) => thread.threadId !== threadId),
        stats: prev.stats ? {
          ...prev.stats,
          totalThreads: Math.max(0, (prev.stats.totalThreads || 0) - 1),
        } : prev.stats,
      };
    });
    setModerationError('');
  }, []);

  const handleThreadModerationDeleted = useCallback((threadId) => {
    handleThreadModerationMoved(threadId);
  }, [handleThreadModerationMoved]);

  const handleRetry = () => {
    setError(null);
    setLoading(true);
    if (forumSlug) {
      forumService.getForumBySlug(forumSlug, {
        page: currentPage,
        limit: 10,
        filters: apiFilters,
      })
        .then((data) => {
          setSingleForumData(data);
          setIsSingleForum(true);
        })
        .catch(() => setError('Erreur lors du chargement des données'))
        .finally(() => setLoading(false));
      return;
    }

    if (universeSlug) {
      universeService.getUniversForums(universeSlug)
        .then((data) => {
          setForumData(data);
        })
        .catch(() => setError('Erreur lors du chargement des données'))
        .finally(() => setLoading(false));
    }
  };

  const handleCreateThread = async () => {
    if (!singleForumData?.forumId || !user) return;

    const title = newThreadTitle.trim();
    const contentContainer = document.createElement('div');
    contentContainer.innerHTML = newThreadContent || '';
    const plainContent = (contentContainer.textContent || '').replace(/\u00a0/g, ' ').trim();
    if (!title || !plainContent) {
      setCreateThreadError('Le titre et le contenu sont requis.');
      return;
    }

    if (isRoleplayForum && !newThreadCharacterId) {
      setCreateThreadError('Sélectionne un personnage pour créer un thread RP.');
      return;
    }

    setCreatingThread(true);
    setCreateThreadError('');
    try {
      if (threadDraftId) {
        await draftService.upsertThreadDraft({
          draftId: threadDraftId,
          forumId: singleForumData.forumId,
          title,
          content: newThreadContent,
          type: isRoleplayForum ? 'roleplay' : 'hrp',
          characterId: isRoleplayForum ? Number(newThreadCharacterId) : null,
          participantCharacterIds: isRoleplayForum
            ? Array.from(new Set([newThreadCharacterId, ...newThreadParticipantIds].filter(Boolean).map(Number)))
            : [],
          factionIds: isRoleplayForum ? newThreadFactionIds.map(Number) : [],
        });
        const published = await draftService.publishThreadDraft(threadDraftId);
        const target = published?.slug || published?.threadId;
        if (target) {
          setThreadDraftId(null);
          setThreadDraftSavedAt(null);
          setThreadDraftMessage('');
          navigate(`/threads/${target}`);
          return;
        }
        setCreateThreadError('Discussion publiée, mais impossible de déterminer sa destination.');
        return;
      }

      const payload = {
        forum_id: singleForumData.forumId,
        author_id: user.id,
        title,
        content: newThreadContent,
        type: isRoleplayForum ? 'roleplay' : 'hrp',
      };

      if (isRoleplayForum) {
        payload.characterId = Number(newThreadCharacterId);
        payload.participantCharacterIds = Array.from(new Set(
          [newThreadCharacterId, ...newThreadParticipantIds].filter(Boolean).map((value) => Number(value))
        ));
        payload.factionIds = newThreadFactionIds.map((value) => Number(value));
      }

      const created = await threadService.createThread(payload);
      const target = created?.slug || created?.threadId;
      if (target) {
        navigate(`/threads/${target}`);
        return;
      }

      setCreateThreadError('Thread créé, mais impossible de déterminer sa destination.');
    } catch (err) {
      setCreateThreadError(err.response?.data?.error || 'Erreur lors de la création du thread.');
    } finally {
      setCreatingThread(false);
    }
  };

  const saveThreadDraft = useCallback(async (isAutomatic = false) => {
    if (!singleForumData?.forumId || !user || !isCreateThreadOpen) return;

    const title = newThreadTitle.trim();
    const hasContent = title !== '' || hasSubstantialHtml(newThreadContent);
    if (!hasContent) return;

    setSavingThreadDraft(true);
    try {
      const saved = await draftService.upsertThreadDraft({
        draftId: threadDraftId || undefined,
        forumId: singleForumData.forumId,
        title: title || 'Sans titre',
        content: newThreadContent,
        type: isRoleplayForum ? 'roleplay' : 'hrp',
        characterId: isRoleplayForum && newThreadCharacterId ? Number(newThreadCharacterId) : null,
        participantCharacterIds: isRoleplayForum
          ? newThreadParticipantIds.filter(Boolean).map(Number)
          : [],
        factionIds: isRoleplayForum ? newThreadFactionIds.map(Number) : [],
      });
      setThreadDraftId(saved.id);
      setThreadDraftSavedAt(saved.updatedAt || new Date().toISOString());
      setThreadDraftMessage(isAutomatic ? 'Brouillon auto-enregistré' : 'Brouillon enregistré');
    } catch (err) {
      if (!isAutomatic) {
        setCreateThreadError(err.response?.data?.error || 'Erreur lors de la sauvegarde du brouillon.');
      }
    } finally {
      setSavingThreadDraft(false);
    }
  }, [
    singleForumData?.forumId,
    user,
    isCreateThreadOpen,
    newThreadTitle,
    newThreadContent,
    threadDraftId,
    isRoleplayForum,
    newThreadCharacterId,
    newThreadParticipantIds,
    newThreadFactionIds,
  ]);

  useEffect(() => {
    if (!user || !singleForumData?.forumId || !isSingleForum) return;

    const resumeDraftId = searchParams.get('resumeDraft');
    let cancelled = false;

    const loadForumDraft = async () => {
      try {
        const data = await draftService.listDrafts();
        if (cancelled) return;

        const drafts = Array.isArray(data?.threadDrafts) ? data.threadDrafts : [];
        let draft = null;
        if (resumeDraftId) {
          draft = drafts.find((item) => String(item.id) === String(resumeDraftId));
          if (draft) {
            setIsCreateThreadOpen(true);
            if (searchParams.has('resumeDraft')) {
              const nextParams = new URLSearchParams(searchParams);
              nextParams.delete('resumeDraft');
              setSearchParams(nextParams, { replace: true });
            }
          }
        } else {
          draft = drafts.find((item) => Number(item.forumId) === Number(singleForumData.forumId));
        }

        if (!draft) return;

        setThreadDraftId(draft.id);
        setNewThreadTitle(draft.title === 'Sans titre' ? '' : (draft.title || ''));
        setNewThreadContent(draft.content || '');
        if (draft.characterId) {
          setNewThreadCharacterId(String(draft.characterId));
        }
        if (Array.isArray(draft.participantCharacterIds)) {
          setNewThreadParticipantIds(draft.participantCharacterIds.map(String));
        }
        if (Array.isArray(draft.factionIds)) {
          setNewThreadFactionIds(draft.factionIds.map(String));
        }
        setThreadDraftSavedAt(draft.updatedAt || null);
        setThreadDraftMessage('Brouillon restauré');
      } catch {
        // ignore draft restore errors
      }
    };

    loadForumDraft();

    return () => {
      cancelled = true;
    };
  }, [user, singleForumData?.forumId, isSingleForum, searchParams, setSearchParams]);

  useEffect(() => {
    if (!isCreateThreadOpen || !user || !singleForumData?.forumId) return undefined;

    const interval = window.setInterval(() => {
      saveThreadDraft(true);
    }, 20000);

    return () => window.clearInterval(interval);
  }, [isCreateThreadOpen, user, singleForumData?.forumId, saveThreadDraft]);

  const filterRoleplayCategories = React.useCallback((categories) => {
    const applySearch = (forums) => {
      if (!searchQuery.trim()) {
        return forums;
      }

      const query = searchQuery.toLowerCase();
      return (forums || []).filter((forum) => {
        const name = (forum.name || '').toLowerCase();
        const description = (forum.description || '').toLowerCase();
        return name.includes(query) || description.includes(query);
      });
    };

    return (categories || [])
      .map((category) => ({
        ...category,
        forums: applySearch(category.forums),
      }))
      .filter((category) => category.forums.length > 0)
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  }, [searchQuery]);

  const filteredRoleplayCategories = useMemo(() => {
    if (activeFilter === FILTER_HRP || activeFilter === FILTER_IMPORTANT) {
      return [];
    }

    const categories = filterRoleplayCategories(forumData?.roleplayCategories);
    if (categories.length > 0) {
      return categories;
    }

    const applySearch = (forums) => {
      if (!searchQuery.trim()) {
        return forums;
      }

      const query = searchQuery.toLowerCase();
      return (forums || []).filter((forum) => {
        const name = (forum.name || '').toLowerCase();
        const description = (forum.description || '').toLowerCase();
        return name.includes(query) || description.includes(query);
      });
    };

    const fallbackForums = applySearch(forumData?.forums?.roleplay || []);
    if (fallbackForums.length === 0) {
      return [];
    }

    return [{
      id: 'roleplay-fallback',
      name: 'Roleplay',
      slug: 'roleplay',
      order: 9999,
      forums: fallbackForums,
    }];
  }, [forumData, activeFilter, filterRoleplayCategories, searchQuery]);

  const filteredForums = useMemo(() => {
    if (!forumData?.forums) return { important: [], playerPlatform: [], hrp: [] };

    const filterByType = (forums, type) => {
      if (activeFilter !== FILTER_ALL && activeFilter !== type) {
        return [];
      }

      if (!searchQuery.trim()) {
        return forums;
      }

      const query = searchQuery.toLowerCase();
      return forums.filter((forum) => {
        const name = (forum.name || '').toLowerCase();
        const description = (forum.description || '').toLowerCase();
        return name.includes(query) || description.includes(query);
      });
    };

    return {
      important: filterByType(forumData.forums.important || [], 'important'),
      playerPlatform: filterByType(forumData.forums.player_platform || [], 'player_platform'),
      hrp: filterByType(forumData.forums.hrp || [], 'hrp'),
    };
  }, [forumData, activeFilter, searchQuery]);

  const forumTypeMeta = useMemo(() => {
    const type = (singleForumData?.type || '').toLowerCase();
    if (type === 'important') {
      return { label: 'Important', className: styles.forumTypeImportant };
    }
    if (type === 'roleplay') {
      return { label: 'RP', className: styles.forumTypeRoleplay };
    }
    if (type === 'player_platform') {
      return { label: 'Plateforme', className: styles.forumTypePlatform };
    }
    if (type === 'hrp') {
      return { label: 'HRP', className: styles.forumTypeHrp };
    }
    if (isRoleplayForum) {
      return { label: 'RP', className: styles.forumTypeRoleplay };
    }
    return { label: 'HRP', className: styles.forumTypeHrp };
  }, [singleForumData?.type, isRoleplayForum]);

  const threadVisualStateById = useMemo(() => {
    const state = new Map();
    (singleForumData?.threads || []).forEach((thread) => {
      state.set(thread.threadId, {
        isUnread: Boolean(thread.isUnread),
        isParticipating: Boolean(thread.isParticipant || thread.userIsParticipant || thread.isUserParticipant),
      });
    });
    return state;
  }, [singleForumData?.threads]);

  const markAllAsRead = async () => {
    try {
      if (isSingleForum && singleForumData?.forumId) {
        await forumService.markForumAsRead(singleForumData.forumId);
        const refreshed = await forumService.getForumBySlug(forumSlug, {
          page: currentPage,
          limit: 10,
          filters: apiFilters,
        });
        setSingleForumData(refreshed);
        return;
      }

      if (isElseworldRoute && universeSlug && elseworldSlug) {
        await universeService.markElseworldAsRead(universeSlug, elseworldSlug);
        const refreshed = await universeService.getElseworldForums(universeSlug, elseworldSlug);
        setForumData(refreshed);
        return;
      }

      if (universeSlug) {
        await universeService.markUniverseAsRead(universeSlug);
        const refreshed = await universeService.getUniversForums(universeSlug);
        setForumData(refreshed);
      }
    } catch (err) {
      console.error(err);
    }
  };

  const hasParticipatingUnreadInForumTree = (forum) => {
    if (!forum) return false;
    const subforums = forum.subforums || forum.subForums || [];
    if (forum.hasParticipatingUnreadThreads) return true;
    return subforums.some((subforum) => hasParticipatingUnreadInForumTree(subforum));
  };

  const hasUnreadInForumTree = (forum) => {
    if (!forum) return false;
    const subforums = forum.subforums || forum.subForums || [];
    if (forum.hasUnreadThreads) return true;
    return subforums.some((subforum) => hasUnreadInForumTree(subforum));
  };

  const universeStats = useMemo(() => {
    if (!forumData?.forums) return null;

    const mainForums = [
      ...(forumData.forums.important || []),
      ...(forumData.forums.player_platform || []),
      ...(forumData.roleplayCategories || []).flatMap((category) => category.forums || []),
      ...(forumData.forums.hrp || []),
    ];
    const allForums = mainForums;

    return allForums.reduce(
      (acc, forum) => ({
        forums: acc.forums + 1 + (forum.subforums?.length || 0),
        threads: acc.threads + (forum.stats?.totalThreads || 0),
        posts: acc.posts + (forum.stats?.totalPosts || 0),
      }),
      { forums: 0, threads: 0, posts: 0 }
    );
  }, [forumData]);

  const filteredActivities = useMemo(() => {
    if (rpActivityFilter === ACTIVITY_FILTER_ALL) return rpActivities;
    return rpActivities.filter((activity) => activity.kind === rpActivityFilter);
  }, [rpActivities, rpActivityFilter]);

  const activitySlides = useMemo(() => {
    if (filteredActivities.length === 0) return [];
    const slides = [];
    for (let index = 0; index < filteredActivities.length; index += ACTIVITY_CARDS_PER_SLIDE) {
      slides.push(filteredActivities.slice(index, index + ACTIVITY_CARDS_PER_SLIDE));
    }
    return slides;
  }, [filteredActivities]);

  useEffect(() => {
    setActivitySlideIndex(0);
  }, [rpActivityFilter]);

  useEffect(() => {
    if (activitySlides.length === 0) {
      setActivitySlideIndex(0);
      return;
    }
    setActivitySlideIndex((prev) => Math.min(prev, activitySlides.length - 1));
  }, [activitySlides]);

  const refreshActivities = async () => {
    if (!currentUniverse || currentUniverse === 'portal') return;
    try {
      const activityData = await rpActivityService.getUniverseActivities(currentUniverse);
      setRpActivities(Array.isArray(activityData?.items) ? activityData.items : []);
    } catch (err) {
      console.error(err);
    }
  };

  if (loading) {
    return (
      <Layout wide>
        <Loading message="Chargement du forum..." />
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout wide>
        <ErrorMessage
          message={error || 'Forum introuvable'}
          onRetry={handleRetry}
        />
      </Layout>
    );
  }

  // Affichage d'un forum spécifique avec ses sous-forums et threads
  if (isSingleForum && singleForumData) {
    const breadcrumbItems = Array.isArray(singleForumData.breadcrumb) && singleForumData.breadcrumb.length > 0
      ? singleForumData.breadcrumb.map((item, index) => {
          const isLegacyHome = index === 0 && typeof item?.name === 'string' && item.name.toLowerCase() === 'home';
          return {
            ...item,
            name: isLegacyHome ? 'FORUMS' : item.name,
          };
        })
      : [
          {
            name: 'FORUMS',
            url: singleForumData.elseworld
              ? getElseworldForumsPath(
                singleForumData.universe?.slug,
                singleForumData.elseworld?.slug,
              )
              : getUniverseForumsPath(universeSlug || singleForumData?.universe?.slug),
          },
          { name: singleForumData.forumName, url: null },
        ];

    return (
      <Layout wide>
        <SeoHead seo={singleForumData.seo} />
        <div className={styles.content}>
          <Breadcrumb items={breadcrumbItems} />

          <header className={styles.header}>
            <div className={styles.headerContent}>
              <div className={styles.headerTop}>
                <div className={styles.titleSection}>
                  <div className={styles.titleRow}>
                    <h1 className={styles.title}>{singleForumData.forumName}</h1>
                    <span className={`${styles.forumTypeBadge} ${forumTypeMeta.className}`}>
                      {forumTypeMeta.label}
                    </span>
                  </div>
                  {singleForumData.description && (
                    <p className={styles.description}>{singleForumData.description}</p>
                  )}
                </div>
                {singleForumData.stats && (
                  <div className={styles.statsSection}>
                    <div className={styles.statItem}>
                      <div className={styles.statValue}>{singleForumData.stats.totalThreads || 0}</div>
                      <div className={styles.statLabel}>Discussions</div>
                    </div>
                    <div className={styles.statItem}>
                      <div className={styles.statValue}>{singleForumData.stats.totalPosts || 0}</div>
                      <div className={styles.statLabel}>Messages</div>
                    </div>
                    {singleForumData.subForums && singleForumData.subForums.length > 0 && (
                      <div className={styles.statItem}>
                        <div className={styles.statValue}>{singleForumData.subForums.length}</div>
                        <div className={styles.statLabel}>Sous-forums</div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </header>

          {/* Sous-forums */}
          {singleForumData.subForums && singleForumData.subForums.length > 0 && (
            <section className={styles.subForumsSection}>
              <h2 className={styles.sectionTitle}>Sous-forums</h2>
              <div className={styles.forumsList}>
                {singleForumData.subForums.map((subForum) => (
                  <ForumCardV3 
                    key={subForum.id}
                    variant={FORUM_CARD_VARIANT_COMPACT}
                    hasUnread={hasUnreadInForumTree(subForum)}
                    hasParticipatingHighlight={hasParticipatingUnreadInForumTree(subForum)}
                    forum={normalizeForum({
                      id: subForum.id,
                      slug: subForum.slug,
                      name: subForum.name,
                      description: subForum.description,
                      banner: subForum.banner,
                      type: subForum.type,
                      hasUnreadThreads: subForum.hasUnreadThreads,
                      hasParticipatingUnreadThreads: subForum.hasParticipatingUnreadThreads,
                      lastPost: subForum.lastPost ? {
                        threadId: subForum.lastPost.threadId,
                        threadSlug: subForum.lastPost.threadSlug,
                        threadTitle: subForum.lastPost.threadTitle,
                        author: subForum.lastPost.author,
                        character: subForum.lastPost.character,
                        avatar: subForum.lastPost.avatar,
                        date: subForum.lastPost.date,
                      } : null,
                      stats: subForum.stats,
                      subForums: [],
                    })} 
                  />
                ))}
              </div>
            </section>
          )}

          {/* Threads */}
          <section className={styles.threadsSection}>
            <div className={styles.sectionHeader}>
              <h2 className={styles.sectionTitle}>Threads</h2>
              <div className={styles.sectionHeaderActions}>
                {singleForumData.pagination && (
                  <span className={styles.threadsCount}>
                    {singleForumData.pagination.total} {singleForumData.pagination.total > 1 ? 'threads' : 'thread'}
                    {singleForumData.pagination.totalPages > 1 && ` • Page ${singleForumData.pagination.page}/${singleForumData.pagination.totalPages}`}
                  </span>
                )}
                {canModerateThreads && (
                  <button
                    type="button"
                    className={`${styles.moderationModeButton} ${isModerationMode ? styles.moderationModeButtonActive : ''}`}
                    onClick={() => {
                      setIsModerationMode((prev) => !prev);
                      setModerationError('');
                    }}
                  >
                    {isModerationMode ? 'Quitter le mode édition' : 'Mode édition'}
                  </button>
                )}
                {isAuthenticated && (
                  <button
                    type="button"
                    className={styles.addThreadButton}
                    onClick={() => setIsCreateThreadOpen((prev) => !prev)}
                  >
                    {isCreateThreadOpen ? 'Fermer' : 'Ajouter un thread'}
                  </button>
                )}
              </div>
            </div>

            {isModerationMode && (
              <p className={styles.moderationModeHint}>
                Mode édition actif : les actions de modération et le déplacement de threads sont disponibles sur chaque ligne.
                {loadingForumMoveOptions && ' Chargement des forums de destination…'}
              </p>
            )}
            {moderationError && <p className={styles.moderationModeError}>{moderationError}</p>}

            {isAuthenticated && isCreateThreadOpen && (
              <div className={styles.createThreadPanel}>
                <div className={styles.createThreadForm}>
                  <input
                    type="text"
                    className={styles.createThreadInput}
                    placeholder="Titre du thread"
                    value={newThreadTitle}
                    onChange={(event) => setNewThreadTitle(event.target.value)}
                  />
                  {isRoleplayForum && (
                    <>
                      <div className={styles.createThreadField}>
                        <label className={styles.createThreadLabel} htmlFor="create-thread-character">
                          Personnage principal
                        </label>
                        <select
                          id="create-thread-character"
                          className={styles.createThreadSelect}
                          value={newThreadCharacterId}
                          onChange={(event) => setNewThreadCharacterId(event.target.value)}
                        >
                          <option value="">Sélectionner un personnage</option>
                          {ownedThreadCharacters.map((character) => (
                            <option key={character.id} value={character.id}>
                              {character.name}
                            </option>
                          ))}
                        </select>
                      </div>
                      <MultiSelectField
                        label="Participants RP"
                        options={universeThreadCharacters}
                        selectedValues={newThreadParticipantIds}
                        onChange={setNewThreadParticipantIds}
                        placeholder="Sélectionner des participants"
                        emptyText="Aucun personnage validé dans cet univers."
                        getOptionLabel={(character) => character.user?.pseudo
                          ? `${character.name} (@${character.user.pseudo})`
                          : character.name}
                      />
                      <MultiSelectField
                        label="Factions impliquées"
                        options={threadFactions}
                        selectedValues={newThreadFactionIds}
                        onChange={setNewThreadFactionIds}
                        placeholder="Sélectionner des factions"
                        emptyText="Aucune faction disponible dans cet univers."
                        getOptionLabel={(faction) => faction.name}
                      />
                    </>
                  )}
                  <div className={styles.createThreadField}>
                    <label className={styles.createThreadLabel}>Premier message</label>
                    {isRoleplayForum && (
                      <DialogueThemePanel
                        idPrefix="create-thread"
                        dialogueThemes={dialogueThemes}
                        onDialogueThemesChange={setDialogueThemes}
                        activeDialogueThemeId={activeDialogueThemeId}
                        onActiveDialogueThemeIdChange={setActiveDialogueThemeId}
                      />
                    )}
                    <RichTextComposer
                      value={newThreadContent}
                      onChange={setNewThreadContent}
                      dialogueThemes={dialogueThemes}
                      activeDialogueThemeId={activeDialogueThemeId}
                      onActiveDialogueThemeIdChange={setActiveDialogueThemeId}
                    />
                  </div>
                  {createThreadError && <p className={styles.createThreadError}>{createThreadError}</p>}
                  {threadDraftMessage && (
                    <p className={styles.createThreadHint}>
                      {threadDraftMessage}
                      {threadDraftSavedAt ? ` - ${new Date(threadDraftSavedAt).toLocaleTimeString('fr-FR')}` : ''}
                    </p>
                  )}
                  <div className={styles.createThreadActions}>
                    <button
                      type="button"
                      className={styles.addThreadButton}
                      onClick={() => saveThreadDraft(false)}
                      disabled={savingThreadDraft || creatingThread}
                    >
                      {savingThreadDraft ? 'Enregistrement...' : 'Enregistrer le brouillon'}
                    </button>
                    <button
                      type="button"
                      className={styles.addThreadButton}
                      onClick={handleCreateThread}
                      disabled={creatingThread}
                    >
                      {creatingThread ? 'Création...' : 'Publier le thread'}
                    </button>
                  </div>
                </div>
              </div>
            )}
            
            {/* Filtres */}
            {isSingleForum && (
              <ThreadFilters
                status={threadFilters.status}
                searchInput={searchInput}
                myParticipations={threadFilters.myParticipations}
                isAuthenticated={isAuthenticated}
                onStatusChange={setStatus}
                onSearchChange={setSearch}
                onMyParticipationsChange={setMyParticipations}
              />
            )}
            {singleForumData.threads && singleForumData.threads.length > 0 ? (
              <div className={`${styles.threadsList} ${isModerationMode ? styles.threadsListModeration : ''}`}>
                {singleForumData.threads.map((thread) => {
                  const threadState = threadVisualStateById.get(thread.threadId) || {
                    isUnread: false,
                    isParticipating: false,
                  };
                  const isParticipatingUnread = threadState.isUnread && threadState.isParticipating;
                  const threadItemClassName = `${styles.threadItem} ${
                    threadState.isUnread ? styles.threadItemUnread : ''
                  } ${isParticipatingUnread ? styles.threadItemParticipatingUnread : ''} ${
                    isModerationMode ? styles.threadItemModeration : ''
                  }`;

                  const threadBody = (
                    <>
                      <div className={styles.threadMainContent}>
                        {thread.authorAvatar && (
                          <img src={thread.authorAvatar} alt={thread.author} className={styles.threadAvatar} />
                        )}
                        <div className={styles.threadContent}>
                          <div className={styles.threadHeader}>
                            <div
                              className={`${styles.threadTitleRow} ${
                                (threadState.isUnread || isParticipatingUnread) ? styles.threadTitleRowWithBadges : ''
                              }`}
                            >
                              <h3 className={styles.threadTitle}>
                                {isModerationMode ? (
                                  <Link
                                    to={`/threads/${thread.threadSlug || thread.threadId}`}
                                    className={styles.threadTitleLink}
                                  >
                                    {thread.title}
                                  </Link>
                                ) : (
                                  thread.title
                                )}
                              </h3>
                              {(threadState.isUnread || isParticipatingUnread) && (
                                <div className={styles.threadStateBadges}>
                                  {threadState.isUnread && (
                                    <span className={styles.badgeUnread}>Non lu</span>
                                  )}
                                  {isParticipatingUnread && (
                                    <span className={styles.badgeParticipating}>Ta participation</span>
                                  )}
                                </div>
                              )}
                            </div>
                            <div className={styles.threadBadges}>
                              <ThreadStatusBadges thread={thread} showType={!isRoleplayForum} />
                            </div>
                          </div>
                          <div className={styles.threadAuthorInfo}>
                            <span className={styles.threadAuthorName}>{thread.author}</span>
                            <span className={styles.separator}>•</span>
                            <span className={styles.threadDate}>{thread.createdAt}</span>
                            {thread.replies !== undefined && (
                              <>
                                <span className={styles.separator}>•</span>
                                <span>{thread.replies} réponses</span>
                              </>
                            )}
                          </div>
                          {isModerationMode && (
                            <ForumThreadModerationActions
                              thread={thread}
                              currentForumId={singleForumData.forumId}
                              forumOptions={forumMoveOptions}
                              onUpdated={handleThreadModerationUpdated}
                              onMoved={handleThreadModerationMoved}
                              onDeleted={handleThreadModerationDeleted}
                              onError={setModerationError}
                            />
                          )}
                        </div>
                      </div>
                      {thread.lastPost && (
                        <div className={styles.lastPostSection}>
                          <div className={styles.lastPostContent}>
                            {thread.lastPost.avatar && (
                              <img
                                src={thread.lastPost.avatar}
                                alt={thread.lastPost.character || thread.lastPost.author}
                                className={styles.lastPostAvatar}
                              />
                            )}
                            <div className={styles.lastPostInfo}>
                              <div className={styles.lastPostLabel}>Dernier message</div>
                              <div className={styles.lastPostAuthor}>
                                {thread.lastPost.character || thread.lastPost.author}
                              </div>
                              {thread.lastPost.date && (
                                <div className={styles.lastPostDate}>
                                  {new Date(thread.lastPost.date).toLocaleDateString('fr-FR', {
                                    day: '2-digit',
                                    month: '2-digit',
                                    year: 'numeric',
                                    hour: '2-digit',
                                    minute: '2-digit',
                                  })}
                                </div>
                              )}
                            </div>
                          </div>
                          {!isModerationMode && (
                            <div className={styles.lastPostIndicator}>
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <polyline points="9 18 15 12 9 6" />
                              </svg>
                            </div>
                          )}
                        </div>
                      )}
                    </>
                  );

                  const threadParticipants = isRoleplayForum && thread.participants?.length > 0 ? (
                    <div className={styles.threadParticipants}>
                      <span className={styles.threadParticipantsLabel}>Avec</span>
                      <div className={styles.threadParticipantsList}>
                        {thread.participants.map((participant) => {
                          const profilePath = getCharacterProfilePath(participant.id);
                          if (!profilePath) {
                            return (
                              <span key={participant.id} className={styles.threadParticipant}>
                                {participant.avatar ? (
                                  <img
                                    src={participant.avatar}
                                    alt=""
                                    className={styles.threadParticipantAvatar}
                                  />
                                ) : (
                                  <span className={styles.threadParticipantFallback}>
                                    {(participant.name || '?').charAt(0).toUpperCase()}
                                  </span>
                                )}
                                <span>{participant.name}</span>
                              </span>
                            );
                          }

                          return (
                            <Link
                              key={participant.id}
                              to={profilePath}
                              className={styles.threadParticipant}
                              onClick={(event) => event.stopPropagation()}
                            >
                              {participant.avatar ? (
                                <img
                                  src={participant.avatar}
                                  alt=""
                                  className={styles.threadParticipantAvatar}
                                />
                              ) : (
                                <span className={styles.threadParticipantFallback}>
                                  {(participant.name || '?').charAt(0).toUpperCase()}
                                </span>
                              )}
                              <span>{participant.name}</span>
                            </Link>
                          );
                        })}
                      </div>
                    </div>
                  ) : null;

                  if (isModerationMode) {
                    return (
                      <div key={thread.threadId} className={threadItemClassName}>
                        {threadBody}
                        {threadParticipants}
                      </div>
                    );
                  }

                  return (
                    <div key={thread.threadId} className={threadItemClassName}>
                      <Link
                        to={`/threads/${thread.threadSlug || thread.threadId}`}
                        className={styles.threadItemNavigate}
                      >
                        {threadBody}
                      </Link>
                      {threadParticipants}
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className={styles.emptyState}>
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                </svg>
                <p>
                  {hasActiveFilters
                    ? 'Aucun thread ne correspond à vos critères de recherche.'
                    : 'Aucun thread dans ce forum pour le moment.'}
                </p>
              </div>
            )}
            
            {/* Pagination */}
            {singleForumData.pagination && singleForumData.pagination.totalPages > 1 && (
              <Pagination
                currentPage={currentPage}
                totalPages={singleForumData.pagination.totalPages}
                onPageChange={setPage}
              />
            )}
          </section>
        </div>
      </Layout>
    );
  }

  // Affichage de la liste des forums de l'univers (comportement existant)
  if (!forumData) {
    return (
      <Layout wide>
        <ErrorMessage
          message="Forum introuvable"
          onRetry={handleRetry}
        />
      </Layout>
    );
  }

  const breadcrumbItems = isElseworldRoute && forumData.elseworld
    ? [
        { name: 'PORTAIL', url: '/', icon: 'home' },
        { name: forumData.universe.name, url: getUniverseForumsPath(forumData.universe.slug), icon: 'forum' },
        { name: forumData.elseworld.name, url: null, icon: 'forum' },
      ]
    : [
        { name: 'PORTAIL', url: '/', icon: 'home' },
        { name: forumData.universe.name, url: null, icon: 'forum' },
      ];

  const hasImportantForums = filteredForums.important.length > 0;
  const hasPlayerPlatformForums = filteredForums.playerPlatform.length > 0;
  const hasRoleplayCategories = filteredRoleplayCategories.length > 0;
  const hasHrpForums = filteredForums.hrp.length > 0;
  const hasAnyContent = hasImportantForums || hasPlayerPlatformForums || hasRoleplayCategories || hasHrpForums
    || (!isElseworldRoute && universeElseworlds.length > 0);

  const isRoleplayForumEntry = (forum) =>
    forum.type === 'roleplay' || forum.isRoleplay === true;

  const getForumCardVariant = (forum) =>
    isRoleplayForumEntry(forum) ? forumCardVariant : FORUM_CARD_VARIANT_COMPACT;

  const getForumsListClassName = ({ compactGap = false } = {}) =>
    [
      styles.forumsList,
      forumLayout === FORUM_LAYOUT_DOUBLE ? styles.forumsListTwoColumns : '',
      compactGap ? styles.forumsListCompact : '',
    ]
      .filter(Boolean)
      .join(' ');

  const renderForumCard = (forum) => {
    const isNonRoleplay = !isRoleplayForumEntry(forum);

    return (
    <ForumCardV3
      key={forum.id}
      compactSubforums={forumLayout === FORUM_LAYOUT_DOUBLE && !isNonRoleplay}
      showAllSubforums={isNonRoleplay}
      useBannerSidebar={isNonRoleplay}
      variant={getForumCardVariant(forum)}
      hasUnread={hasUnreadInForumTree(forum)}
      hasParticipatingHighlight={hasParticipatingUnreadInForumTree(forum)}
      forum={normalizeForum({
        ...forum,
        lastThread: forum.lastPost ? {
          id: forum.lastPost.threadId,
          title: forum.lastPost.threadTitle,
          author: forum.lastPost.author || forum.lastPost.character,
          date: forum.lastPost.date,
        } : null,
        stats: forum.stats,
        subForums: forum.subforums || [],
      })}
    />
    );
  };

  const renderRoleplayCategoryBlock = (category) => {
    const forumCount = category.forums?.length ?? 0;
    const forumCountLabel = `${forumCount} forum${forumCount > 1 ? 's' : ''}`;

    return (
      <div
        key={category.id}
        className={styles.roleplayCategoryBlock}
        id={`category-${category.slug}`}
      >
        <div className={styles.roleplayCategoryHeader}>
          <div className={styles.roleplayCategoryHeading}>
            <span className={styles.roleplayCategoryEyebrow}>
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                <circle cx="9" cy="7" r="4" />
                <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                <path d="M16 3.13a4 4 0 0 1 0 7.75" />
              </svg>
              Forums Roleplay
            </span>
            <h3 className={styles.roleplayCategoryTitle}>{category.name}</h3>
            {category.description && (
              <p className={styles.roleplayCategoryDescription}>{category.description}</p>
            )}
          </div>
          <span className={styles.roleplayCategoryCount}>{forumCountLabel}</span>
        </div>
        <div
          className={`${styles.forumsList} ${
            forumLayout === FORUM_LAYOUT_DOUBLE ? styles.forumsListTwoColumns : ''
          }`}
        >
          {category.forums.map((forum) => renderForumCard(forum))}
        </div>
      </div>
    );
  };

  const headerBannerUrl = isElseworldRoute
    ? forumData.elseworld?.banner
    : forumData.universe.forumsHeaderBanner;
  const forumsPageTitle = isElseworldRoute && forumData.elseworld
    ? `Forums de ${forumData.elseworld.name}`
    : (forumData.universe.forumsTitle?.trim() || `Forums de ${forumData.universe.name}`);
  const forumsPageDescription = isElseworldRoute && forumData.elseworld
    ? forumData.elseworld.description
    : forumData.universe.description;
  const headerContentClassName = headerBannerUrl
    ? `${styles.headerContent} ${styles.headerContentBanner}`
    : styles.headerContent;

  return (
    <Layout wide>
      <SeoHead seo={pageSeo} />
      <div className={styles.content}>
        {/* Header */}
        <header className={styles.header}>
          <div
            className={headerContentClassName}
            style={headerBannerUrl ? { '--header-banner-image': `url(${headerBannerUrl})` } : undefined}
          >
            <Breadcrumb items={breadcrumbItems} variant="compact" />
            <div className={styles.titleRow}>
              {isElseworldRoute && forumData.elseworld?.logo && (
                <img
                  src={forumData.elseworld.logo}
                  alt={forumData.elseworld.name}
                  className={styles.elseworldHeaderLogo}
                />
              )}
              <h1 className={styles.title}>{forumsPageTitle}</h1>
            </div>
            {forumsPageDescription && (
              <p className={styles.description}>{forumsPageDescription}</p>
            )}
            {universeStats && (
              <div className={styles.headerStats}>
                <span className={styles.headerStat}>
                  <strong>{universeStats.forums}</strong> forums
                </span>
                <span className={styles.headerStatSeparator}>•</span>
                <span className={styles.headerStat}>
                  <strong>{universeStats.threads}</strong> discussions
                </span>
                <span className={styles.headerStatSeparator}>•</span>
                <span className={styles.headerStat}>
                  <strong>{universeStats.posts}</strong> messages
                </span>
              </div>
            )}
          </div>
        </header>

        {/* Section masquée quand aucun event/mission n'existe (univers principal uniquement) */}
        {!isElseworldRoute && (rpActivitiesLoading || rpActivities.length > 0) && (
        <section className={styles.activitiesSection}>
          <div className={styles.sectionHeader}>
            <div>
              <h2 className={styles.sectionTitle}>Events & Missions RP</h2>
              <p className={styles.sectionSubtitle}>Inscriptions par personnage et relances planifiées.</p>
            </div>
            <div className={styles.filterButtons}>
              <button
                className={`${styles.filterButton} ${rpActivityFilter === ACTIVITY_FILTER_ALL ? styles.active : ''}`}
                onClick={() => setRpActivityFilter(ACTIVITY_FILTER_ALL)}
                type="button"
              >
                Tous
              </button>
              <button
                className={`${styles.filterButton} ${rpActivityFilter === ACTIVITY_FILTER_EVENT ? styles.active : ''}`}
                onClick={() => setRpActivityFilter(ACTIVITY_FILTER_EVENT)}
                type="button"
              >
                Events
              </button>
              <button
                className={`${styles.filterButton} ${rpActivityFilter === ACTIVITY_FILTER_MISSION ? styles.active : ''}`}
                onClick={() => setRpActivityFilter(ACTIVITY_FILTER_MISSION)}
                type="button"
              >
                Missions
              </button>
            </div>
          </div>

          {rpActivitiesLoading ? (
            <p className={styles.activitiesLoading}>Chargement des activités RP...</p>
          ) : filteredActivities.length === 0 ? (
            <p className={styles.activitiesEmpty}>Aucun event ou mission pour ce filtre.</p>
          ) : (
            <div className={styles.activitiesSlider}>
              {activitySlides.length > 1 && (
                <div className={styles.activitiesSliderHeader}>
                  <p className={styles.activitiesSliderMeta}>
                    Slide {activitySlideIndex + 1} / {activitySlides.length}
                  </p>
                  <div className={styles.activitiesSliderControls}>
                    <button
                      type="button"
                      className={styles.activitiesNavButton}
                      onClick={() => setActivitySlideIndex((prev) => Math.max(0, prev - 1))}
                      disabled={activitySlideIndex === 0}
                      aria-label="Slide précédent"
                    >
                      ‹
                    </button>
                    <button
                      type="button"
                      className={styles.activitiesNavButton}
                      onClick={() => setActivitySlideIndex((prev) => Math.min(activitySlides.length - 1, prev + 1))}
                      disabled={activitySlideIndex === activitySlides.length - 1}
                      aria-label="Slide suivant"
                    >
                      ›
                    </button>
                  </div>
                </div>
              )}

              <div className={styles.activitiesViewport}>
                <div
                  className={styles.activitiesTrack}
                  style={{ transform: `translateX(-${activitySlideIndex * 100}%)` }}
                >
                  {activitySlides.map((slideItems, slideIdx) => (
                    <div key={`activity-slide-${slideIdx}`} className={styles.activitiesSlide}>
                      <div
                        className={styles.activitiesGrid}
                      >
                        {slideItems.map((activity) => (
                          <RpActivityCard
                            key={activity.id}
                            activity={activity}
                            selectableCharacters={activitySelectableCharacters}
                            showRegistration={false}
                            onRegistrationChanged={() => { void refreshActivities(); }}
                          />
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </section>
        )}

        {/* Système de filtres */}
        <div className={styles.filtersSection}>
          <div className={styles.filtersToolbar}>
            <div className={styles.filtersControls}>
              <div className={styles.controlGroup}>
                <span className={styles.controlLabel}>Type</span>
                <FilterToggle
                  options={FORUM_TYPE_OPTIONS}
                  value={activeFilter}
                  onChange={setActiveFilter}
                  ariaLabel="Type de forum"
                />
              </div>
              <div className={styles.controlGroup}>
                <span className={styles.controlLabel}>Vue</span>
                <FilterToggle
                  options={FORUM_LAYOUT_OPTIONS}
                  value={forumLayout}
                  onChange={setForumLayout}
                  ariaLabel="Disposition des forums"
                />
              </div>
              <div className={styles.controlGroup}>
                <span className={styles.controlLabel}>Affichage</span>
                <FilterToggle
                  options={FORUM_VARIANT_OPTIONS}
                  value={forumCardVariant}
                  onChange={setForumCardVariant}
                  ariaLabel="Présentation des cartes"
                />
              </div>
            </div>
            <div className={styles.toolbarActions}>
              <div className={styles.searchBox}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="11" cy="11" r="8" />
                  <path d="m21 21-4.35-4.35" />
                </svg>
                <input
                  type="text"
                  placeholder="Rechercher..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className={styles.searchInput}
                />
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery('')}
                    className={styles.clearButton}
                    aria-label="Effacer la recherche"
                    type="button"
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <line x1="18" y1="6" x2="6" y2="18" />
                      <line x1="6" y1="6" x2="18" y2="18" />
                    </svg>
                  </button>
                )}
              </div>
              <button
                type="button"
                className={styles.markAllReadButton}
                onClick={() => { void markAllAsRead(); }}
                aria-label="Tout marquer comme lu"
                title="Tout marquer comme lu"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                  <polyline points="22 4 12 14.01 9 11.01" />
                </svg>
              </button>
            </div>
          </div>
        </div>

        {/* Forums Importants */}
        {hasImportantForums && (
          <section className={styles.forumTypeSection} id="important-forums">
            <div className={styles.sectionHeader}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
              </svg>
              <div>
                <h2 className={styles.sectionTitle}>Forums Importants</h2>
                <p className={styles.sectionSubtitle}>Annonces et informations essentielles de {forumData.universe.name}</p>
              </div>
            </div>
            <div className={getForumsListClassName({ compactGap: true })}>
              {filteredForums.important.map((forum) => renderForumCard(forum))}
            </div>
          </section>
        )}

        {/* Plateforme joueur */}
        {hasPlayerPlatformForums && (
          <section className={styles.forumTypeSection} id="platform-forums">
            <div className={styles.sectionHeader}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M3 12h18" />
                <path d="M12 3v18" />
                <path d="m8 8 8 8" />
                <path d="m16 8-8 8" />
              </svg>
              <div>
                <h2 className={styles.sectionTitle}>Plateforme joueur</h2>
                <p className={styles.sectionSubtitle}>Espaces joueurs et ressources hors RP de {forumData.universe.name}</p>
              </div>
            </div>
            <div className={getForumsListClassName({ compactGap: true })}>
              {filteredForums.playerPlatform.map((forum) => renderForumCard(forum))}
            </div>
          </section>
        )}

        {/* Forums Roleplay par catégorie */}
        {hasRoleplayCategories && (
          <section className={styles.forumTypeSection} id="rp-forums">
            <div className={styles.sectionHeader}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                <circle cx="9" cy="7" r="4" />
                <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                <path d="M16 3.13a4 4 0 0 1 0 7.75" />
              </svg>
              <div>
                <h2 className={styles.sectionTitle}>Forums Roleplay</h2>
                <p className={styles.sectionSubtitle}>
                  Incarnez vos personnages dans {isElseworldRoute && forumData.elseworld
                    ? `l'elseworld ${forumData.elseworld.name}`
                    : `l'univers ${forumData.universe.name}`}
                </p>
              </div>
            </div>
            <div className={styles.roleplayCategoriesList}>
              {filteredRoleplayCategories.map((category) => renderRoleplayCategoryBlock(category))}
            </div>
          </section>
        )}

        {!isElseworldRoute && universeElseworlds.length > 0 && (
          <section className={styles.elseworldNavSection} id="elseworlds-section">
            <div className={styles.sectionHeader}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10" />
                <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
              </svg>
              <div>
                <h2 className={styles.sectionTitle}>Elseworlds</h2>
                <p className={styles.sectionSubtitle}>
                  Réalités alternatives de {forumData.universe.name}
                </p>
              </div>
            </div>
            <div className={styles.forumsList}>
              {universeElseworlds.map((elseworld) => (
                <ElseworldCard
                  key={elseworld.id}
                  elseworld={elseworld}
                  universeSlug={forumData.universe.slug}
                  hasUnread={elseworld.hasUnreadThreads}
                  hasParticipatingHighlight={elseworld.hasParticipatingUnreadThreads}
                />
              ))}
            </div>
          </section>
        )}


        {/* Forums Hors-Roleplay */}
        {hasHrpForums && (
          <section className={styles.forumTypeSection} id="hrp-forums">
            <div className={styles.sectionHeader}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
              </svg>
              <div>
                <h2 className={styles.sectionTitle}>Forums Hors-Roleplay</h2>
                <p className={styles.sectionSubtitle}>Discussions et informations globales du site</p>
              </div>
            </div>
            <div className={getForumsListClassName({ compactGap: true })}>
              {filteredForums.hrp.map((forum) => renderForumCard(forum))}
            </div>
          </section>
        )}

        {/* Message si aucun résultat */}
        {!hasAnyContent && (
          <div className={styles.emptyState}>
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="11" cy="11" r="8" />
              <path d="m21 21-4.35-4.35" />
            </svg>
            <p>Aucun forum trouvé avec ces critères de recherche.</p>
            <button
              onClick={() => {
                setActiveFilter(FILTER_ALL);
                setSearchQuery('');
              }}
              className={styles.resetButton}
            >
              Réinitialiser les filtres
            </button>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default ForumDetail;
